import { callRpc, listRows } from '@/api/resources';
import { getSupabase } from '@/lib/supabase';
import type { GeographyHit } from '@/lib/geographyPath';

export interface Option { id: string; label: string }
export interface GeographyOption extends Option { geography_type?: string; parent_id?: string | null }

/**
 * Pickers. These are the only reads that deliberately bypass the grid's record scope: a role
 * scoped to `own` must still be able to REFERENCE a farmer it did not create, or the three
 * required dropdowns on a plot form come back empty. The database decides how far each picker
 * reaches (`picker_scope`, default `all`); geography and tenant always apply.
 */
export function farmerOptions(search?: string, villageId?: string) {
  return callRpc<Array<Option & { farmer_code: string; village_id: string; status: string }>>(
    'fn_farmer_options',
    { p_search: search || null, p_village_id: villageId || null },
  );
}

export function plotOptions(farmerId?: string, search?: string) {
  return callRpc<Array<Option & { plot_code: string; farmer_id: string; village_id: string; status: string }>>(
    'fn_plot_options',
    { p_farmer_id: farmerId || null, p_search: search || null },
  );
}

export function partyOptions(roleType?: string, search?: string) {
  return callRpc<Array<Option & { party_code: string; party_category: string; status: string }>>(
    'fn_party_options',
    { p_role_type: roleType || null, p_search: search || null },
  );
}

export async function geographyOptions(type: string, parentId?: string, search?: string, limit?: number): Promise<GeographyOption[]> {
  // The RPC returns `name`; every dropdown here reads `label`. Without this mapping each state,
  // district and village rendered as a blank option.
  const rows = await callRpc<Array<{ id: string; name: string; local_name: string | null; parent_id: string | null; geography_type: string }>>(
    'fn_geography_options', {
      p_geography_type: type,
      p_parent_id: parentId || null,
      p_search: search || null,
      ...(limit ? { p_limit: limit } : {}),
    });
  return (rows ?? []).map((row) => ({
    id: row.id,
    label: row.local_name && row.local_name !== row.name ? `${row.name} (${row.local_name})` : row.name,
    geography_type: row.geography_type,
    parent_id: row.parent_id,
  }));
}

/**
 * The places of one type under ANY of the parents given (owner, 2026-09-25: "multiselect of
 * districts or places. start from state, district, sub-district, villages in sequence").
 *
 * One request for a whole level of the cascade, rather than one per box the officer ticked. Each
 * row carries the parent that matched it, so a list can be grouped by district rather than run
 * together.
 */
export async function geographyChildren(
  parentIds: string[],
  type: string,
  limit = 500,
): Promise<Array<GeographyOption & { matched_parent_id: string | null }>> {
  if (!parentIds.length) return [];
  const rows = await callRpc<Array<{ id: string; name: string; local_name: string | null; parent_id: string | null;
                                     geography_type: string; matched_parent_id: string | null }>>(
    'fn_geography_children', { p_parent_ids: parentIds, p_geography_type: type, p_limit: limit });
  return (rows ?? []).map((row) => ({
    id: row.id,
    label: row.local_name && row.local_name !== row.name ? `${row.name} (${row.local_name})` : row.name,
    geography_type: row.geography_type,
    parent_id: row.parent_id,
    matched_parent_id: row.matched_parent_id,
  }));
}

// The path helpers live in `@/lib/geographyPath`: pure, no network, and unit-tested there.
// Re-exported so the many callers of `@/api/lookups` need not care where they moved to.
export { geographyParents, geographyPath } from '@/lib/geographyPath';
export type { GeographyHit } from '@/lib/geographyPath';

/**
 * Places matching free text at ANY level above them, so "Eluru" finds the villages of Eluru
 * district and "Adivinekkalam" finds the village. Narrowed to what the organisation has activated
 * and to the caller's picker scope, by the same rules as the cascading lists.
 */
export async function geographySearch(search: string, type = 'village', limit = 30): Promise<GeographyHit[]> {
  const rows = await callRpc<GeographyHit[]>('fn_geography_search', {
    p_search: search, p_geography_type: type, p_limit: limit,
  });
  return rows ?? [];
}

/** The one place an id names, for showing a record that already has a village. */
export async function geographyById(id: string): Promise<GeographyHit | null> {
  const { data, error } = await getSupabase().schema('pub').from('geography_master')
    .select('id, name, local_name, geography_type, state_id, district_id, block_id').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const parents = [data.block_id, data.district_id, data.state_id].filter(Boolean) as string[];
  const names = new Map<string, string>();
  if (parents.length) {
    const { data: rows } = await getSupabase().schema('pub').from('geography_master')
      .select('id, name').in('id', parents);
    for (const row of rows ?? []) names.set(row.id as string, row.name as string);
  }
  return {
    id: data.id as string,
    name: data.name as string,
    local_name: (data.local_name as string | null) ?? null,
    // Without the level, a settled state falls to the village branch and prints its own name
    // twice - the chip read "West Bengal, West Bengal".
    geography_type: (data.geography_type as string | null) ?? null,
    block_name: data.block_id ? names.get(data.block_id as string) ?? null : null,
    district_name: data.district_id ? names.get(data.district_id as string) ?? null : null,
    state_name: data.state_id ? names.get(data.state_id as string) ?? null : null,
  };
}

export interface GeographyAncestry { id: string; state_id: string | null; district_id: string | null; block_id: string | null }

/**
 * Where one geography sits: its state, district and block. Used to back-fill the cascading
 * pickers when an existing record is opened. The option lists cannot answer this - they carry
 * no parent columns and are capped - so it is read from the public master by id.
 */
export async function geographyAncestry(id: string): Promise<GeographyAncestry | null> {
  const { data, error } = await getSupabase().schema('pub').from('geography_master')
    .select('id, state_id, district_id, block_id').eq('id', id).maybeSingle();
  if (error) throw new Error(error.message);
  return data as GeographyAncestry | null;
}

/**
 * Every state in the master, activated or not. A hectare factor belongs to the state itself -
 * a Bigha is the size it is in Uttar Pradesh whether or not this organisation works there yet -
 * so the factor pickers must not be narrowed to the organisation's activated places.
 */
export async function allStates(): Promise<Option[]> {
  const { data, error } = await getSupabase().schema('pub').from('geography_master')
    .select('id, name').eq('geography_type', 'state').order('name');
  if (error) throw new Error(error.message);
  return (data ?? []).filter((row) => row.id && row.name).map((row) => ({ id: row.id as string, label: row.name as string }));
}

/** The name of the state a geography sits in (itself, if it is a state). */
export async function stateNameOf(geographyId: string): Promise<string | null> {
  const place = await geographyAncestry(geographyId);
  const stateId = place?.state_id ?? geographyId;
  const { data, error } = await getSupabase().schema('pub').from('geography_master')
    .select('name').eq('id', stateId).maybeSingle();
  if (error) throw new Error(error.message);
  return (data as { name: string | null } | null)?.name ?? null;
}

/** "Village, District, State" for a village, to centre a map on it. */
export async function placeLabelOf(villageId: string): Promise<string | null> {
  const place = await geographyAncestry(villageId);
  const ids = [villageId, place?.district_id, place?.state_id].filter((id): id is string => Boolean(id));
  const { data, error } = await getSupabase().schema('pub').from('geography_master').select('id, name').in('id', ids);
  if (error) throw new Error(error.message);
  const nameOf = (id?: string | null) => (data ?? []).find((row: { id: string | null }) => row.id === id)?.name ?? null;
  const parts = [nameOf(villageId), nameOf(place?.district_id), nameOf(place?.state_id)].filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

export interface ReferenceValue { id: string; value_code: string; label: string; set_key: string; attributes?: Record<string, unknown> | null }

/**
 * Reference values by set key. The live sets are `species`, `unit_of_measure`,
 * `land_tenure_type`, `kyc_type`, `commodity` and `finance_category` - a key that matches
 * nothing returns an empty list rather than failing, which is how a dropdown ends up silently
 * blank, so `unitOptions` below narrows an existing set instead of inventing a new one.
 */
export function referenceValues(setKey: string) {
  return listRows<ReferenceValue>('reference_values', {
    filters: { set_key: setKey },
    orderBy: { column: 'label' },
  });
}

export interface AreaUnit { id: string; value_code: string; label: string; is_fixed: boolean }

/**
 * There is ONE unit set, holding area and volume units together, and each unit's
 * `attributes.kind` says which it measures (migration 0036). Area units come from the
 * `area_units` view, which also says whether the unit is a fixed size (hectare, acre) or varies
 * by place (bigha, katha) - the plot form needs to know, because a varying unit converts to
 * hectares only where a factor for that state has been set.
 */
export function areaUnits() {
  return listRows<AreaUnit>('area_units', { orderBy: { column: 'sort_order' } });
}

/**
 * Units by what they measure (`attributes.kind`): area, volume, or - for how much a harvest
 * yielded - any quantity: weight, count or volume (migration 0074).
 */
export async function unitOptions(kind: 'area' | 'volume' | 'quantity'): Promise<ReferenceValue[]> {
  const [all, area] = await Promise.all([referenceValues('unit_of_measure'), areaUnits()]);
  const areaIds = new Set(area.map((unit) => unit.id));
  const kindOf = (unit: ReferenceValue) => String(unit.attributes?.kind ?? (areaIds.has(unit.id) ? 'area' : 'volume'));
  if (kind === 'area') return all.filter((unit) => areaIds.has(unit.id));
  if (kind === 'volume') return all.filter((unit) => kindOf(unit) === 'volume');
  return all.filter((unit) => ['weight', 'count', 'volume'].includes(kindOf(unit)));
}

export interface AreaConversion { hectares: number | null; factor: number | null; rule_status: string | null }

/** What the plot form will store as hectares, and with which factor. Null when no factor applies. */
export async function convertArea(value: number, unitId: string, villageId: string): Promise<AreaConversion | null> {
  const rows = await callRpc<AreaConversion[]>('fn_convert_area', {
    p_value: value, p_unit_id: unitId, p_village_id: villageId,
  });
  return rows?.[0] ?? null;
}
